import type { Coding } from '@medplum/fhirtypes';
import { CODE_SYSTEMS } from './constants';

/**
 * The cancer-screening categories a Prevention topic tracks, in display order. Every category is
 * returned by the grouper whether or not the patient has anything on file, so a gap is visible.
 */
export const SCREENING_CATEGORIES = ['colorectal-cancer', 'breast-cancer', 'cervical-cancer'] as const;
export type ScreeningCategoryCode = (typeof SCREENING_CATEGORIES)[number];

/**
 * The studies a screening can be done with, in display order within their category. A modality is
 * the unit a guideline puts an interval on (colonoscopy every ten years, FIT yearly), so it is the
 * unit a row is keyed by.
 */
export const SCREENING_MODALITIES = [
  'colonoscopy',
  'fit',
  'stool-dna',
  'fobt',
  'flexible-sigmoidoscopy',
  'ct-colonography',
  'mammogram',
  'breast-mri',
  'pap',
  'hpv',
] as const;
export type ScreeningModalityCode = (typeof SCREENING_MODALITIES)[number];

/**
 * How a result `Observation`'s value is read into an outcome.
 *
 * - `bi-rads` — the BI-RADS assessment category (0–6) on a breast imaging result.
 * - `detected` — a presence test: negative / not detected against positive / detected.
 * - `bethesda` — cervical cytology in Bethesda terms (NILM, ASC-US, LSIL, HSIL, …).
 * - `none` — the result lives only in a narrative report; nothing structured can be read.
 */
export type ScreeningOutcomeRule = 'bi-rads' | 'detected' | 'bethesda' | 'none';

/** A coding as a search key: system and code only. */
export type ScreeningCoding = Pick<Coding, 'system' | 'code'>;

export interface ScreeningModality {
  code: ScreeningModalityCode;
  category: ScreeningCategoryCode;
  /** Codings that name the study itself, as carried on a `Procedure` or a `DiagnosticReport`. */
  studyCodes: readonly ScreeningCoding[];
  /** Codings that name the result, as carried on an `Observation`. */
  resultCodes: readonly ScreeningCoding[];
  /**
   * Codings on companion `Observation`s a report carries beside the result (adequacy, a comment, the
   * date of the previous study). Never events in their own right; a comment is read when the result
   * itself only says "see note".
   */
  companionCodes: readonly ScreeningCoding[];
  /**
   * Phrases that place an uncoded row, or a row coded in a source-local vocabulary, on this
   * modality. Matched at a word start, case-insensitively; a trailing `*` marks a stem that matches
   * any word beginning with it (`colonoscop*` reaches "colonoscopy" and "colonoscopies"). When a
   * text matches several modalities the longest matching alias wins, so "immuno fecal occult blood"
   * is FIT rather than guaiac FOBT.
   */
  textAliases: readonly string[];
  outcomeRule: ScreeningOutcomeRule;
}

/** AMA CPT, as Epic exports carry it. */
export const CPT_SYSTEM = 'http://www.ama-assn.org/go/cpt';
/** CPT by OID, the other spelling a C-CDA conversion produces. */
export const CPT_OID_SYSTEM = 'urn:oid:2.16.840.1.113883.6.12';
/** CMS HCPCS level II by OID (`G` and `Q` codes). */
export const HCPCS_OID_SYSTEM = 'urn:oid:2.16.840.1.113883.6.14';
/**
 * The Epic-local vocabulary the production population's BI-RADS category and gastric occult-blood
 * results are coded in. Epic OIDs embed the customer, so another Epic site would carry a different
 * OID for the same concepts; the text aliases catch those.
 */
export const EPIC_LOCAL_RESULT_SYSTEM = 'urn:oid:1.2.840.114350.1.13.32.2.7.2.768282';

const loinc = (code: string): ScreeningCoding => ({ system: CODE_SYSTEMS.LOINC, code });
const snomed = (code: string): ScreeningCoding => ({ system: CODE_SYSTEMS.SNOMED, code });
const cpt = (code: string): ScreeningCoding[] => [
  { system: CPT_SYSTEM, code },
  { system: CPT_OID_SYSTEM, code },
];
const hcpcs = (code: string): ScreeningCoding => ({ system: HCPCS_OID_SYSTEM, code });
const epic = (code: string): ScreeningCoding => ({ system: EPIC_LOCAL_RESULT_SYSTEM, code });

/**
 * The screening concept table. Codes are the ones seen on the production population (Epic C-CDA
 * exports relayed by Metriport) plus the standard screening codes for the same studies; a code
 * not listed here is reached by text alias, so the table is a precision aid rather than a gate.
 */
export const SCREENING_MODALITY_TABLE: readonly ScreeningModality[] = [
  {
    code: 'colonoscopy',
    category: 'colorectal-cancer',
    studyCodes: [
      snomed('73761001'),
      ...cpt('45378'),
      ...cpt('45380'),
      ...cpt('45385'),
      hcpcs('G0105'),
      hcpcs('G0121'),
      loinc('18746-8'),
    ],
    resultCodes: [],
    companionCodes: [],
    textAliases: ['colonoscop*'],
    outcomeRule: 'none',
  },
  {
    code: 'fit',
    category: 'colorectal-cancer',
    studyCodes: [...cpt('82274'), hcpcs('G0328')],
    resultCodes: [loinc('29771-3')],
    companionCodes: [loinc('56850-1')],
    textAliases: ['fit', 'ifobt', 'immunochemical', 'immuno fecal occult*', 'fecal immuno*'],
    outcomeRule: 'detected',
  },
  {
    code: 'stool-dna',
    category: 'colorectal-cancer',
    studyCodes: [...cpt('81528')],
    resultCodes: [loinc('77354-9')],
    companionCodes: [],
    textAliases: ['cologuard', 'stool dna', 'sdna', 'fit-dna', 'fecal dna', 'multitarget stool*'],
    outcomeRule: 'detected',
  },
  {
    code: 'fobt',
    category: 'colorectal-cancer',
    studyCodes: [...cpt('82270'), ...cpt('82272')],
    resultCodes: [loinc('2335-8')],
    companionCodes: [],
    textAliases: ['occult blood', 'guaiac', 'hemoccult'],
    outcomeRule: 'detected',
  },
  {
    code: 'flexible-sigmoidoscopy',
    category: 'colorectal-cancer',
    studyCodes: [...cpt('45330'), ...cpt('45331'), hcpcs('G0104'), loinc('18744-3')],
    resultCodes: [],
    companionCodes: [],
    textAliases: ['sigmoidoscop*', 'flex sig'],
    outcomeRule: 'none',
  },
  {
    code: 'ct-colonography',
    category: 'colorectal-cancer',
    studyCodes: [...cpt('74261'), ...cpt('74262'), ...cpt('74263')],
    resultCodes: [],
    companionCodes: [],
    textAliases: ['colonograph*', 'virtual colonoscop*'],
    outcomeRule: 'none',
  },
  {
    code: 'mammogram',
    category: 'breast-cancer',
    studyCodes: [...cpt('77067'), ...cpt('77066'), ...cpt('77065'), ...cpt('77063'), loinc('26175-0')],
    resultCodes: [epic('7754')],
    companionCodes: [],
    textAliases: ['mammogra*', 'tomosynthesis', 'bi-rads*', 'birads*'],
    outcomeRule: 'bi-rads',
  },
  {
    code: 'breast-mri',
    category: 'breast-cancer',
    studyCodes: [...cpt('77049'), ...cpt('77048'), loinc('36276-4')],
    resultCodes: [],
    companionCodes: [],
    textAliases: ['breast mri', 'mri breast', 'mr breast', 'mri of the breast', 'mri of breast'],
    outcomeRule: 'bi-rads',
  },
  {
    code: 'pap',
    category: 'cervical-cancer',
    studyCodes: [...cpt('88175'), ...cpt('88142'), hcpcs('Q0091'), loinc('33717-0')],
    resultCodes: [loinc('10524-7'), loinc('47527-7')],
    companionCodes: [loinc('19764-0'), loinc('19774-9'), loinc('60432-2')],
    textAliases: [
      'pap',
      'pap smear',
      'pap test',
      'papanicolaou',
      'thin prep*',
      'thinprep*',
      'cervical cytolog*',
      'cervical or vaginal',
      'cervical smear',
    ],
    outcomeRule: 'bethesda',
  },
  {
    code: 'hpv',
    category: 'cervical-cancer',
    studyCodes: [...cpt('87624'), ...cpt('87621'), ...cpt('87625')],
    resultCodes: [loinc('69002-4'), loinc('30167-1'), loinc('77399-4'), loinc('59420-0')],
    companionCodes: [],
    textAliases: ['hpv', 'papilloma*'],
    outcomeRule: 'detected',
  },
];

/**
 * Rows a name or code lookup would otherwise sweep in that are not screenings. Epic's gastric
 * occult-blood results (`trace`, `small`) match any "occult blood" text; its health-maintenance
 * topics ("HM Mammogram") and order placeholders ("ORDER COLONOSCOPY") are bookkeeping, not results.
 * Checked before any modality match.
 */
export const SCREENING_EXCLUSIONS = {
  codes: [epic('1672'), epic('21157')] as readonly ScreeningCoding[],
  textAliases: ['gastric', 'gastroccult', 'no data available'] as readonly string[],
  /** A name beginning with one of these is excluded whatever follows. */
  textPrefixes: ['hm ', 'order '] as readonly string[],
} as const;

const MODALITY_BY_CODE = new Map<ScreeningModalityCode, ScreeningModality>(
  SCREENING_MODALITY_TABLE.map((modality) => [modality.code, modality]),
);

const MODALITY_POSITION = new Map<ScreeningModalityCode, number>(
  SCREENING_MODALITIES.map((code, index) => [code, index]),
);

const CATEGORY_POSITION = new Map<ScreeningCategoryCode, number>(
  SCREENING_CATEGORIES.map((code, index) => [code, index]),
);

export const getScreeningModality = (code: ScreeningModalityCode): ScreeningModality => {
  const modality = MODALITY_BY_CODE.get(code);
  if (!modality) throw new Error(`Unknown screening modality: ${code}`);
  return modality;
};

/** Declaration position, for ordering rows the way the table lists them. */
export const screeningModalityPosition = (code: ScreeningModalityCode): number => MODALITY_POSITION.get(code) ?? -1;

export const screeningCategoryPosition = (code: ScreeningCategoryCode): number => CATEGORY_POSITION.get(code) ?? -1;

/** Modalities of one category, in display order. */
export const screeningModalitiesOf = (category: ScreeningCategoryCode): ScreeningModality[] =>
  SCREENING_MODALITY_TABLE.filter((modality) => modality.category === category);

/** English display names for the server and the AI context; hosts with an i18n tree translate the codes. */
export const SCREENING_CATEGORY_NAMES: Record<ScreeningCategoryCode, string> = {
  'colorectal-cancer': 'Colorectal cancer screening',
  'breast-cancer': 'Breast cancer screening',
  'cervical-cancer': 'Cervical cancer screening',
};

export const SCREENING_MODALITY_NAMES: Record<ScreeningModalityCode, string> = {
  colonoscopy: 'Colonoscopy',
  fit: 'FIT',
  'stool-dna': 'Stool DNA (Cologuard)',
  fobt: 'Guaiac FOBT',
  'flexible-sigmoidoscopy': 'Flexible sigmoidoscopy',
  'ct-colonography': 'CT colonography',
  mammogram: 'Mammogram',
  'breast-mri': 'Breast MRI',
  pap: 'Pap test',
  hpv: 'HPV test',
};

const normalizeText = (value: string): string => value.trim().toLowerCase().replace(/\s+/g, ' ');

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const aliasPattern = (alias: string): RegExp => {
  const stem = alias.endsWith('*');
  const literal = escapeRegExp(stem ? alias.slice(0, -1) : alias);
  return new RegExp(`(^|[^a-z0-9])${literal}${stem ? '' : '($|[^a-z0-9])'}`);
};

/** Whether normalised `text` contains `alias` at a word start (and, for a non-stem, a word end). */
export const screeningAliasMatches = (text: string, alias: string): boolean =>
  aliasPattern(normalizeText(alias)).test(normalizeText(text));

const codingKey = (coding: ScreeningCoding): string => `${coding.system ?? ''}|${coding.code ?? ''}`;

const STUDY_MODALITY_BY_CODING = new Map<string, ScreeningModality>(
  SCREENING_MODALITY_TABLE.flatMap((modality) => modality.studyCodes.map((c) => [codingKey(c), modality] as const)),
);
const RESULT_MODALITY_BY_CODING = new Map<string, ScreeningModality>(
  SCREENING_MODALITY_TABLE.flatMap((modality) => modality.resultCodes.map((c) => [codingKey(c), modality] as const)),
);
const COMPANION_MODALITY_BY_CODING = new Map<string, ScreeningModality>(
  SCREENING_MODALITY_TABLE.flatMap((modality) => modality.companionCodes.map((c) => [codingKey(c), modality] as const)),
);
const EXCLUDED_CODINGS = new Set(SCREENING_EXCLUSIONS.codes.map(codingKey));

/** How a record was placed on a modality: by a study code, a result code, or a text alias. */
export type ScreeningMatchKind = 'study' | 'result' | 'companion' | 'text';

export interface ScreeningMatch {
  modality: ScreeningModality;
  kind: ScreeningMatchKind;
}

/** Whether a name or coding marks the row as one the table excludes. */
export const isExcludedScreeningRecord = (codings: readonly Coding[], names: readonly string[]): boolean => {
  if (codings.some((coding) => EXCLUDED_CODINGS.has(codingKey(coding)))) return true;
  const text = normalizeText(names.join(' | '));
  if (!text) return false;
  if (
    names.some((name) => SCREENING_EXCLUSIONS.textPrefixes.some((prefix) => normalizeText(name).startsWith(prefix)))
  ) {
    return true;
  }
  return SCREENING_EXCLUSIONS.textAliases.some((alias) => screeningAliasMatches(text, alias));
};

const matchByText = (names: readonly string[]): ScreeningModality | undefined => {
  const text = normalizeText(names.join(' | '));
  if (!text) return undefined;
  let best: { modality: ScreeningModality; length: number } | undefined;
  for (const modality of SCREENING_MODALITY_TABLE) {
    for (const alias of modality.textAliases) {
      if (!screeningAliasMatches(text, alias)) continue;
      const length = alias.replace(/\*$/, '').length;
      if (!best || length > best.length) best = { modality, length };
    }
  }
  return best?.modality;
};

/**
 * Resolve the codings and names a record carries to a screening modality. Codes are tried first
 * (study, then result, then companion), and text aliases only when no coding is in the table, so a
 * coded row is never re-routed by a loose word in its display. Excluded rows resolve to nothing.
 */
export const matchScreeningModality = (
  codings: readonly Coding[],
  names: readonly string[],
): ScreeningMatch | undefined => {
  if (isExcludedScreeningRecord(codings, names)) return undefined;
  for (const coding of codings) {
    const study = STUDY_MODALITY_BY_CODING.get(codingKey(coding));
    if (study) return { modality: study, kind: 'study' };
  }
  for (const coding of codings) {
    const result = RESULT_MODALITY_BY_CODING.get(codingKey(coding));
    if (result) return { modality: result, kind: 'result' };
  }
  for (const coding of codings) {
    const companion = COMPANION_MODALITY_BY_CODING.get(codingKey(coding));
    if (companion) return { modality: companion, kind: 'companion' };
  }
  const byText = matchByText(names);
  return byText ? { modality: byText, kind: 'text' } : undefined;
};
